import DeleteIcon from "@mui/icons-material/Delete";
import ReplayIcon from "@mui/icons-material/Replay";
import { CircularProgress } from "@mui/material";
import classNames from "classnames";
import EditableTable from "components/EditableTable";
import { type TCell, type TColumn } from "components/EditableTable/types";
import StyledButton from "components/shared/StyledButton";
import { WhiteDropdownMenu } from "components/shared/WhiteDropdownMenu";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TFunction } from "react-i18next";
import { useTranslation } from "react-i18next";
import {
    deleteImportRowsWithErrors,
    getImportTableErrorsCount,
    getTable,
    updateImportSalesRows,
} from "store/dataImportSlice";
import { fetchAnomaliesReportAsFile, fetchImportedFile, fetchImportJobOfProcessingJob } from "store/filesHistorySlice";
import type { ImportJobStep, Job } from "store/jobsStore";
import { useAppDispatch } from "store/store";

import variables from "../../../assets/styles/_variables.module.scss";
import type { TImportDataDictionary } from "../type";
import { getCorrectionPanelConfig } from "./configs";

// Extended cell type to include additional metadata
type ExtendedTCell = TCell & {
    initialValue?: string;
    isModified?: boolean;
};

type ErrorLine = {
    fileLine: string;
    mappedKey: string;
    code?: string;
    message?: string;
};

export interface CorrectionPanelConfig {
    columns: TColumn[];
    contentType: string;
    updateAction?: any; // Redux action for updating rows
}

type GenericEditableTableRow = {
    id: string;
    columns: Record<string, ExtendedTCell>;
};

/**
 * Calculation-base columns whose imported name differs from the one they are stored in, so the
 * mapping chosen by the user is still found when building the correction table.
 */
const MAPPING_KEY_BY_STORED_COLUMN: Record<string, string> = {
    qty: "quantity",
    revenue: "turnover",
};

function parseImportData(rows: Array<Record<string, any>>, columnsConfig: TColumn[]): GenericEditableTableRow[] {
    return rows.map((row) => {
        const modifiedFields =
            (row.modified_fields as Record<string, { initialValue: string | number; lastModified: string }>) || {};

        const parsedRow: GenericEditableTableRow = {
            id: String(row.importIndex),
            columns: {},
        };

        columnsConfig.forEach(({ key }) => {
            const value = row[key];

            const originalKey = key.startsWith("input_") ? key.replace("input_", "") : key; // Remove 'input_' prefix if present
            const modifiedData = modifiedFields[originalKey];

            parsedRow.columns[key] = {
                value: String(value ?? ""),
                isModified: !!modifiedData,
                initialValue: String(modifiedData?.initialValue ?? value ?? ""),
            } as ExtendedTCell;
        });

        return parsedRow;
    });
}

function getTotals(
    salesData: Array<{ total_rows?: number; total_error_lines?: number; total_correction_lines?: number }>,
) {
    const firstRow = salesData[0];
    return {
        totalRows: firstRow?.total_rows,
        totalErrors: firstRow?.total_error_lines,
        totalCorrectionLinesCount: firstRow?.total_correction_lines,
    };
}

function enhanceRowsWithErrorsAndModifications(
    tableData: GenericEditableTableRow[],
    currentErrors: ErrorLine[],
    columnsConfig: TColumn[],
    renderedColumnKeys: Set<string>,
    t: TFunction<"translation", undefined>,
): GenericEditableTableRow[] {
    return tableData.map((row) => {
        const rowErrors = currentErrors.filter((error) => `${error.fileLine}` === `${row.id}`);

        const enhancedRow: GenericEditableTableRow = {
            id: row.id,
            columns: { ...row.columns },
        };

        // Mark modified cells as editable with green background
        columnsConfig.forEach(({ key }) => {
            const cellData = row.columns[key] as ExtendedTCell | undefined;
            if (cellData?.isModified) {
                enhancedRow.columns[key] = {
                    ...cellData,
                    isEditable: true,
                    helperText: t("ImportData.Corrected"),
                    sx: {
                        padding: "6px",
                        backgroundColor: "rgba(83, 197, 83, 0.2)",
                    },
                };
            }
        });

        rowErrors.forEach((error) => {
            const columnKey = error.mappedKey;
            // If the target column is not rendered (e.g. missing from the CSV), fall back to
            // the importIndex cell so the error message is always visible to the user.
            const targetKey = columnKey && renderedColumnKeys.has(columnKey) ? columnKey : "importIndex";

            const errorMessage =
                t(`ImportData.Errors.${error.code}`) !== `ImportData.Errors.${error.code}`
                    ? t(`ImportData.Errors.${error.code}`)
                    : error.message ?? "Unknown error";

            const existing = enhancedRow.columns[targetKey];
            enhancedRow.columns[targetKey] = {
                ...existing,
                isEditable: targetKey !== "importIndex",
                helperText: existing?.helperText ? `${existing.helperText} | ${errorMessage}` : errorMessage,
                sx: {
                    padding: "6px",
                    backgroundColor: "rgba(197, 83, 83, 0.2)",
                },
            };
        });

        return enhancedRow;
    });
}

const CorrectionPanel = ({
    processingJobId,
    onTableStatus,
    contentType,
}: {
    processingJobId: number;
    onTableStatus?: (status: { step: ImportJobStep | null }) => void;
    contentType: TImportDataDictionary;
}) => {
    // Get the configuration based on contentType
    const config = useMemo(() => getCorrectionPanelConfig(contentType), [contentType]);

    const [t] = useTranslation();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [tableData, setTableData] = useState<GenericEditableTableRow[]>([]);
    const [currentErrors, setCurrentErrors] = useState<ErrorLine[]>([]);
    const [rowsCount, setRowsCount] = useState<number>();
    const [errorsCount, setErrorsCount] = useState<number>();
    const [currentJobStep, setCurrentJobStep] = useState<ImportJobStep | null>(null);
    const [columnsMap, setColumnsMap] = useState<Record<string, string>>();
    const [importJobId, setImportJobId] = useState<number | null>(null);
    /** False until import job metadata (columnsMap) is fetched — avoids rendering the table with empty columns while rows already loaded (race after full page refresh). */
    const [importJobMetadataLoaded, setImportJobMetadataLoaded] = useState(false);
    const [editsPending, setEditsPending] = useState<boolean>(false);
    const [errorsCountPending, setErrorsCountPending] = useState<boolean>(false);

    const [pagination, setPagination] = useState({
        page: 0,
        limit: 25,
        total: 0,
    });

    const columns = useMemo(() => {
        if (!columnsMap) {
            return [];
        }

        return config.columns.reduce<TColumn[]>(
            (acc, column) => {
                const k = column.key.startsWith("input_") ? column.key.replace("input_", "") : column.key; // Remove 'input_' prefix if present

                const mappedKey = columnsMap[k] ?? columnsMap[MAPPING_KEY_BY_STORED_COLUMN[k]];

                if (mappedKey && mappedKey !== "NotMandatory") {
                    acc.push({
                        ...column,
                        label: mappedKey,
                    });
                }

                return acc;
            },
            [
                {
                    key: "importIndex",
                    label: "n°",
                    size: 50,
                },
            ],
        );
    }, [config.columns, columnsMap]);

    const dispatch = useAppDispatch();
    const pendingUpdates = useRef<Map<string, Partial<Record<string, { value: string | number }>>>>(new Map());
    const processingJobIdRef = useRef(processingJobId);
    processingJobIdRef.current = processingJobId;

    const fetchErrorsCount = useCallback(async () => {
        setErrorsCountPending(true);
        const response = await dispatch(
            getImportTableErrorsCount({
                jobId: processingJobId,
            }),
        );

        if (response.meta.requestStatus === "fulfilled") {
            const { errorsCount, correctionLinesCount } = response.payload.data as {
                errorsCount: number;
                correctionLinesCount: number;
            };
            setErrorsCount(errorsCount);
            setPagination((prev) => ({
                ...prev,
                total: correctionLinesCount,
            }));
        } else {
            console.error("Failed to fetch errors count");
        }

        setErrorsCountPending(false);
    }, [dispatch, processingJobId]);

    // Fetch table data from API
    const fetchTableData = useCallback(
        async (
            jobId: number,
            pagination?: {
                page?: number;
                limit?: number;
            },
        ) => {
            try {
                setIsLoading(true);

                const limit = pagination?.limit || 50;
                const page = pagination?.page || 0;
                const offset = page * limit;

                const response = await dispatch(
                    getTable({
                        jobId,
                        filters: { offset, limit, correctionTableLinesOnly: true },
                    }),
                );

                if (response.meta.requestStatus === "fulfilled") {
                    const { jobData, importTable } = response.payload.data as {
                        jobData: Job;
                        importTable: {
                            rows: Array<Record<string, any>>;
                            errors?: ErrorLine[];
                        };
                    };

                    if (!importTable || !importTable.rows) {
                        throw new Error("Invalid import table data");
                    }

                    setCurrentJobStep(jobData.metadata?.step || null);

                    const { rows, errors = [] } = importTable;
                    const parsedRows = parseImportData(rows, config.columns);
                    const { totalRows, totalErrors, totalCorrectionLinesCount } = getTotals(rows);

                    return {
                        rows: parsedRows,
                        errors,
                        totalRows,
                        totalErrors,
                        totalCorrectionLinesCount,
                        pageSize: limit,
                        page,
                    };
                } else {
                    throw new Error("Failed to fetch table data");
                }
            } catch (error) {
                console.error("Error fetching table data:", error);
                throw error;
            } finally {
                setIsLoading(false);
            }
        },
        [dispatch, config.columns],
    );

    const fetchPageOfTableData = useCallback(
        async (jobId: number, page: number, limit: number) => {
            try {
                const { rows, errors, totalRows, totalErrors, totalCorrectionLinesCount, pageSize } =
                    await fetchTableData(jobId, {
                        page,
                        limit,
                    });

                setTableData(rows);
                setCurrentErrors(errors);
                if (totalRows !== undefined) {
                    setRowsCount(totalRows);
                }
                if (totalErrors !== undefined) {
                    setErrorsCount(totalErrors);
                }
                setPagination((prev) => {
                    const fallbackTotal =
                        rows.length === pageSize ? (page + 1) * pageSize + 1 : page * pageSize + rows.length;
                    return {
                        ...prev,
                        total: totalCorrectionLinesCount ?? prev.total ?? fallbackTotal,
                    };
                });
            } catch (error) {
                console.error("Error fetching page of table data:", error);
            }
        },
        [fetchTableData],
    );

    const fetchImportJobData = useCallback(
        async (jobId: number) => {
            try {
                const response = await dispatch(fetchImportJobOfProcessingJob(jobId));

                if (response.meta.requestStatus === "fulfilled") {
                    const jobData = response.payload.data;

                    if (jobId !== processingJobIdRef.current) {
                        return;
                    }

                    if (jobData?.metadata?.columnsMap) {
                        setColumnsMap(jobData?.metadata?.columnsMap);
                    }

                    if (jobData?.id) {
                        setImportJobId(jobData.id);
                    }
                } else {
                    throw new Error("Failed to fetch import job ID");
                }
            } catch (error) {
                console.error("Error fetching import job ID:", error);

                return null;
            } finally {
                if (jobId === processingJobIdRef.current) {
                    setImportJobMetadataLoaded(true);
                }
            }
        },
        [dispatch],
    );

    const debounceFetchErrorsCount = useRef(debounce(fetchErrorsCount, 1500));
    useEffect(() => {
        debounceFetchErrorsCount.current = debounce(fetchErrorsCount, 1500);
        return () => {
            debounceFetchErrorsCount.current.cancel();
        };
    }, [fetchErrorsCount]);

    // Update rows on server
    const updateRowsOnServer = useCallback(async () => {
        if (pendingUpdates.current.size === 0) return;

        const updates = Array.from(pendingUpdates.current.entries()).map(([id, changes]) => ({
            id,
            ...Object.fromEntries(Object.entries(changes).map(([key, data]) => [key, data?.value ?? ""])),
        }));

        pendingUpdates.current.clear();

        try {
            const response = await dispatch(
                updateImportSalesRows({
                    jobId: processingJobId,
                    rows: updates,
                }),
            );

            if (response.meta.requestStatus === "fulfilled" && response.payload.data?.importData) {
                const { jobData, importData } = response.payload.data as {
                    jobData: Job;
                    importData: {
                        rows?: Array<Record<string, any>>;
                        errors?: ErrorLine[];
                        totalErrors: number;
                    };
                };

                const { rows, errors = [] } = importData;

                setCurrentJobStep(jobData.metadata?.step || null);

                if (rows) {
                    const updatedRows = parseImportData(rows, config.columns);

                    // Update table data for the modified rows
                    setTableData((prev) => {
                        const newData = [...prev];
                        updatedRows.forEach((updatedRow) => {
                            const index = newData.findIndex((row) => row.id === updatedRow.id);
                            if (index !== -1) {
                                newData[index] = updatedRow;
                            }
                        });
                        return newData;
                    });
                }

                // Update errors - merge with existing errors for other rows
                setCurrentErrors((prev) => {
                    const updatedRowIds = new Set(updates.map((u) => String(u.id)));
                    // Keep errors for rows that weren't updated
                    const errorsForOtherRows = prev.filter((error) => !updatedRowIds.has(String(error.fileLine)));
                    // Add new errors for the updated rows
                    return [...errorsForOtherRows, ...errors];
                });

                debounceFetchErrorsCount.current();
            }
        } catch (error) {
            console.error("Error updating rows:", error);
        }
    }, [dispatch, processingJobId, config.columns]);

    // Debounced update function
    const debouncedUpdate = useRef(debounce(updateRowsOnServer, 1000));

    useEffect(() => {
        debouncedUpdate.current = debounce(updateRowsOnServer, 1000);
        return () => {
            debouncedUpdate.current.cancel();
        };
    }, [updateRowsOnServer]);

    // Handle cell updates
    const handleCellUpdate = useCallback(
        ({
            rowId,
            rowIndex,
            columnId,
            value,
        }: {
            rowId: string | number;
            rowIndex: number;
            columnId: string;
            value: string | number;
        }) => {
            const stringRowId = String(rowId);
            const columnKey = columnId;
            const stringValue = String(value);

            setTableData((prev) => {
                const newData = [...prev];
                if (newData[rowIndex] && newData[rowIndex].columns[columnKey]) {
                    const currentCell = newData[rowIndex].columns[columnKey]!;
                    newData[rowIndex].columns[columnKey] = {
                        ...currentCell,
                        value: stringValue,
                    };
                }
                return newData;
            });

            // Queue update for server - store the clean string value, not the original value
            const currentUpdates = pendingUpdates.current.get(stringRowId) || {};
            pendingUpdates.current.set(stringRowId, {
                ...currentUpdates,
                [columnKey]: { value: stringValue },
            });

            setErrorsCountPending(true);
            debouncedUpdate.current();
        },
        [],
    );

    const handleRowDelete = useCallback(
        (rowId: string | number, rowIndex: number) => () => {
            handleCellUpdate({
                rowId,
                rowIndex,
                columnId: "input_isDeleted",
                value: "1",
            });
        },
        [handleCellUpdate],
    );

    const handleRestoreDeletedRow = useCallback(
        (rowId: string | number, rowIndex: number) => () => {
            handleCellUpdate({
                rowId,
                rowIndex,
                columnId: "input_isDeleted",
                value: "0",
            });
        },
        [handleCellUpdate],
    );

    const handleDeleteAllRowsWithErrors = useCallback(async () => {
        if (editsPending || isLoading) {
            return;
        }

        setEditsPending(true);
        setIsLoading(true);
        setErrorsCountPending(true);

        pendingUpdates.current?.clear?.();
        debouncedUpdate.current?.cancel?.();

        try {
            await dispatch(
                deleteImportRowsWithErrors({
                    jobId: processingJobId,
                }),
            );

            await fetchPageOfTableData(processingJobId, pagination.page, pagination.limit);
            await fetchErrorsCount();
        } catch (error) {
            //
        } finally {
            setEditsPending(false);
            setIsLoading(false);
            setErrorsCountPending(false);
        }
    }, [
        dispatch,
        processingJobId,
        editsPending,
        isLoading,
        fetchErrorsCount,
        fetchPageOfTableData,
        pagination.page,
        pagination.limit,
    ]);

    const rowBackgroundColor = useCallback((row: GenericEditableTableRow) => {
        if (row.columns.input_isDeleted?.value === "1") {
            return "#adadad";
        }
    }, []);

    const renderedColumnKeys = useMemo(() => new Set(columns.map((c) => c.key)), [columns]);

    // Enhanced table rows with error styling and editability
    const enhancedTableRows = useMemo(() => {
        return enhanceRowsWithErrorsAndModifications(tableData, currentErrors, config.columns, renderedColumnKeys, t);
    }, [tableData, currentErrors, config.columns, renderedColumnKeys]);

    // Load data on page change
    useEffect(() => {
        fetchPageOfTableData(processingJobId, pagination.page, pagination.limit);
    }, [processingJobId, pagination.page, pagination.limit, fetchPageOfTableData]);

    useEffect(() => {
        setImportJobMetadataLoaded(false);
    }, [processingJobId]);

    useEffect(() => {
        fetchImportJobData(processingJobId);
    }, [processingJobId, fetchImportJobData]);

    useEffect(() => {
        fetchErrorsCount();
    }, [fetchErrorsCount]);

    // Notify parent of status changes
    useEffect(() => {
        if (!onTableStatus) return;

        onTableStatus({ step: currentJobStep });
    }, [isLoading, currentJobStep, onTableStatus]);

    const tableUiLoading = isLoading || !importJobMetadataLoaded;
    const showTableSection = tableUiLoading || tableData.length > 0;

    return (
        <div className="relative h-full flex flex-col">
            <div className="flex flex-row items-start justify-between px-2 pb-2!">
                <div>
                    <div className="flex-none relative h[2lh] my-1">
                        {rowsCount !== undefined && <span>{t("ImportData.LinesToImport", { count: rowsCount })}</span>}
                    </div>
                    <div
                        className={classNames("flex-none relative h[2lh] my-1", {
                            "opacity-50": errorsCountPending,
                        })}
                    >
                        {!tableUiLoading && (
                            <div className="flex items-center gap-2">
                                <span
                                    className={classNames({
                                        "text-red-500!": errorsCount,
                                        "text-green-500!": !errorsCount,
                                    })}
                                >
                                    {t("ImportData.InvalidLines", { count: errorsCount || 0 })}
                                </span>
                                {errorsCountPending && (
                                    <CircularProgress
                                        size="14px"
                                        className={classNames({
                                            "text-red-500!": errorsCount,
                                            "text-green-500!": !errorsCount,
                                        })}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex flex-row items-center gap-2 py-2">
                    <StyledButton
                        className="action-btn"
                        buttonType="danger"
                        disabled={editsPending}
                        onClick={() => {
                            handleDeleteAllRowsWithErrors();
                        }}
                    >
                        {editsPending ? (
                            <CircularProgress
                                size="22px"
                                sx={{
                                    color: variables["primary-purple"],
                                }}
                            />
                        ) : (
                            <DeleteIcon />
                        )}
                        {t("ImportData.DeleteAllErrorLines")}
                    </StyledButton>
                    <WhiteDropdownMenu
                        options={[
                            {
                                id: 1,
                                name: t("ImportData.DownloadImportFile"),
                                action: () => {
                                    if (importJobId) {
                                        dispatch(fetchImportedFile(importJobId));
                                    }
                                },
                            },
                        ]}
                        buttonName={t("ImportData.DownloadAnomaliesReport")}
                        minWidth="120px"
                        disableMainAction={!importJobId}
                        mainAction={() => {
                            if (importJobId) {
                                dispatch(fetchAnomaliesReportAsFile({ importId: importJobId }));
                            }
                        }}
                    />
                </div>
            </div>

            {showTableSection ? (
                <div className="flex-1 overflow-auto">
                    <EditableTable
                        rows={enhancedTableRows}
                        columns={columns}
                        pagination={pagination}
                        isLoading={tableUiLoading}
                        onPageChange={(page: number) => {
                            console.log("Page changed to:", page);
                            setPagination((prev) => ({
                                ...prev,
                                page,
                            }));
                        }}
                        onPageLimitChange={(limit: number) => {
                            setPagination((prev) => ({
                                ...prev,
                                limit,
                                page: 0, // Reset to first page on limit change
                            }));
                        }}
                        rowBackgroundColor={rowBackgroundColor as any}
                        renderRowActions={({ row }) => {
                            const isRowDeleted = row.original.columns.input_isDeleted?.value === "1";

                            return (
                                <button
                                    className="border-none bg-transparent cursor-pointer"
                                    onClick={
                                        isRowDeleted
                                            ? handleRestoreDeletedRow(row.original.id, row.index)
                                            : handleRowDelete(row.original.id, row.index)
                                    }
                                >
                                    {isRowDeleted ? (
                                        <div className="flex flex-col items-center justify-center">
                                            <ReplayIcon className="text-black" />
                                            <span className="text-[0.6rem]!">{t("ImportData.DeletedLine")}</span>
                                        </div>
                                    ) : (
                                        <DeleteIcon className="text-[#ed6e6e]" />
                                    )}
                                </button>
                            );
                        }}
                        onCellUpdate={handleCellUpdate}
                    />
                </div>
            ) : (
                <div className="h-full flex flex-col items-center justify-center gap-2">
                    <div className="flex justify-center mt-3!">{t("ImportData.NoCorrectionsNeeded")}</div>
                </div>
            )}
        </div>
    );
};

CorrectionPanel.displayName = "CorrectionPanel";

export default CorrectionPanel;
